import Link from 'next/link';
import type { Post, ContentCategory } from '@/lib/content-source';
import { calculateReadingTime, formatReadingTime } from '@/lib/reading-time';
import ViewCounter from './ViewCounter';

interface ArticleCardProps {
  post: Post;
  category: ContentCategory;
}

export default function ArticleCard({ post, category }: ArticleCardProps) {
  const readingTime = calculateReadingTime(post.content);
  const readingTimeLabel = formatReadingTime(readingTime);
  return (
    <article className="border border-border rounded-lg hover:border-border-muted0 transition-colors relative">
      {/* Main clickable area - single link wrapping title and excerpt */}
      <Link
        href={`/${category}/${post.slug}`}
        className="block p-6 pb-4"
      >
        {/* Category, Featured Badge & Premium Badge */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-accent text-sm uppercase tracking-wider" aria-label={`Category: ${category}`}>
            {category}
          </span>
          {post.featured && (
            <span className="bg-accent-muted text-accent text-xs px-2 py-1 rounded" aria-label="Featured article">
              Featured
            </span>
          )}
          {post.premium && (
            <span
              className="bg-accent/30 text-accent text-xs px-2 py-1 rounded flex items-center gap-1 font-semibold"
              aria-label={`Premium content - ${post.requiredTier || 'pro'} plan required`}
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              {post.requiredTier === 'team' ? 'Team' : 'Pro'}
            </span>
          )}
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold mb-2 text-light-text dark:text-foreground group-hover:text-accent transition-colors">
          {post.title}
        </h2>

        {/* Excerpt */}
        <p className="text-light-text-secondary dark:text-foreground-secondary mb-4">{post.excerpt}</p>
      </Link>

      {/* Meta: Date & Tags - Outside main link to avoid nested links */}
      <div className="flex items-center justify-between text-sm text-light-text-secondary dark:text-foreground-secondary px-6 pb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <time dateTime={post.date}>
            {new Date(post.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </time>
          <span>•</span>
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {readingTimeLabel}
          </span>
          <span>•</span>
          <ViewCounter slug={post.slug} category={category} increment={false} />
        </div>
        <div className="flex gap-2">
          {post.tags.slice(0, 3).map((tag) => (
            <Link
              key={tag}
              href={`/tags/${encodeURIComponent(tag.toLowerCase())}`}
              className="text-accent hover:text-accent transition-colors relative z-10"
              aria-label={`View posts tagged with ${tag}`}
            >
              #{tag}
            </Link>
          ))}
        </div>
      </div>
    </article>
  );
}
